import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import flatpickr from 'flatpickr';
import { addIcons } from 'ionicons';
import * as icons from 'ionicons/icons';
import { Subscription } from 'rxjs';
import { DefaultConfig } from 'src/assets/default.config';
import { ChatbotDialogComponent } from './components/dialogs/chatbot/chatbot-dialog.component';
import { MenuBarModule } from './components/menu-bar/menu-bar.module';
import { UserAppSettingsService } from './services/app-settings/user-app-settings.service';
import { BackendAvailabilityService } from './services/backend-availability/backend-availability.service';
import { CopilotSidebarService } from './services/copilot-sidebar/copilot-sidebar.service';
import { LogService } from './services/loggers/logger.service';
import { MemoryUsageService } from './services/memory-logger';
import { NavigationHistoryService } from './services/navigation-history/navigation-history.service';
import { SoftwareUpdateService } from './services/software-update-service/software-update.service';
import { UserService } from './services/user/user.service';
import { NgxTranslateModule } from './translate.module';
import { NotificationHelper } from './utility/notification';
import { UNUSED_ARGS } from './utility/unused-arguments';

export function addUsedIcons() {
  const usedIcons: (keyof typeof icons)[] = [
    'add',
    'addCircleOutline',
    'addOutline',
    'alert',
    'alertCircleOutline',
    'alertOutline',
    'analyticsOutline',
    'archiveOutline',
    'arrowBackOutline',
    'arrowForwardOutline',
    'arrowUpOutline',
    'banOutline',
    'batteryChargingOutline',
    'batteryDeadOutline',
    'batteryFullOutline',
    'batteryHalfOutline',
    'bookmark',
    'bookmarkOutline',
    'calendarOutline',
    'calendarClearOutline',
    'caretDownOutline',
    'caretForwardOutline',
    'cellularOutline',
    'chatboxOutline',
    'chatbubbleEllipsesOutline',
    'chatbubbleOutline',
    'chevronDownOutline',
    'checkmark',
    'checkmarkCircle',
    'checkmarkCircleOutline',
    'checkmarkDoneOutline',
    'checkmarkOutline',
    'chevronBackOutline',
    'chevronDown',
    'chevronDownOutline',
    'chevronForward',
    'chevronForwardOutline',
    'chevronUpOutline',
    'closeCircleOutline',
    'closeOutline',
    'cogOutline',
    'contractOutline',
    'copy',
    'copyOutline',
    'desktopOutline',
    'documentAttachOutline',
    'documentLockOutline',
    'documentTextOutline',
    'downloadOutline',
    'ellipsisVertical',
    'ellipsisVerticalOutline',
    'exitOutline',
    'expandOutline',
    'eyeOffOutline',
    'eyeOutline',
    'information',
    'fileTrayFullOutline',
    'fileTrayOutline',
    'fileTrayStackedOutline',
    'flashOutline',
    'flaskOutline',
    'folderOutline',
    'happyOutline',
    'helpCircleOutline',
    'helpOutline',
    'informationCircle',
    'informationCircleOutline',
    'link',
    'linkOutline',
    'lockClosed',
    'lockClosedOutline',
    'lockOpenOutline',
    'menuOutline',
    'notificationsOutline',
    'openOutline',
    'pencilOutline',
    'peopleOutline',
    'person',
    'personOutline',
    'pricetagOutline',
    'playOutline',
    'pauseOutline',
    'stopOutline',
    'prismOutline',
    'refreshOutline',
    'removeCircleOutline',
    'removeOutline',
    'reorderTwoOutline',
    'saveOutline',
    'search',
    'searchOutline',
    'sendOutline',
    'settingsOutline',
    'thumbsDown',
    'thumbsDownOutline',
    'thumbsUp',
    'thumbsUpOutline',
    'timeOutline',
    'todayOutline',
    'trashBinOutline',
    'trashOutline',
    'unlink',
    'unlinkOutline',
    'warningOutline',
  ];
  addIcons(Object.fromEntries(usedIcons.map(iconName => [iconName, icons[iconName]])));
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: true,
  imports: [
    AsyncPipe,
    RouterModule,
    MenuBarModule,
    NgxTranslateModule,
    ChatbotDialogComponent,
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly copilotSidebarService = inject(CopilotSidebarService);
  protected readonly isCopilotOpen$ = this.copilotSidebarService.isOpen$;

  private readonly appSettings = inject(UserAppSettingsService);
  private readonly backendAvailability = inject(BackendAvailabilityService);
  private readonly log = inject(LogService);
  private readonly memoryUsageService = inject(MemoryUsageService);
  private readonly softwareUpdateService = inject(SoftwareUpdateService);
  private readonly userService = inject(UserService);

  /** lives here (used or unused) so the navigation history is tracked right from the beginning */
  private readonly navigationHistoryService = inject(NavigationHistoryService);

  private copilotBodyClassSub: Subscription | undefined;

  constructor() {
    UNUSED_ARGS(this.appSettings);
    let ignoreCheckingBackendAvailability = false;
    const pathname = window.location.pathname;
    if (DefaultConfig.keycloak.disclaimerPagePath === pathname || pathname.startsWith(DefaultConfig.keycloak.disclaimerPagePath)) {
      ignoreCheckingBackendAvailability = true;
    }

    if (!ignoreCheckingBackendAvailability) {
      this.backendAvailability.startChecking();
    }
    this.log.info('Browser Data: ' + navigator.userAgent);
    addUsedIcons();
  }

  public ngOnInit() {
    this.persistCopilotDisclaimerAcceptance();

    this.copilotBodyClassSub = this.isCopilotOpen$.subscribe(isOpen => {
      document.body.classList.toggle('copilot-sidebar-open', isOpen);
    });

    // log memory usage to backend log with defined interval
    const fiveMinutesInMillis = 300_000;
    void this.memoryUsageService.logMemoryUsagePeriodically(fiveMinutesInMillis);

    setTimeout(
      () => void this.runUpdateCheck(),
      DefaultConfig.general.autoSoftwareUpdateDelay_Seconds * 1000
    );

    setTimeout(() => {
      try {
        NotificationHelper.checkForExpiredMutedNotifications();
      } catch (err) {
        this.log.error('Failed to clean up expired muted notifications', err);
      }
    });
  }

  public ngOnDestroy() {
    this.copilotBodyClassSub?.unsubscribe();
    document.body.classList.remove('copilot-sidebar-open');
  }

  private async runUpdateCheck() {
    try {
      await this.softwareUpdateService.runSoftwareUpdateCheck();
    } catch (err) {
      this.log.error('Failed to attempt initial software update check', err);
    }
  }

  /**
   * Bridges the pre-login disclaimer acceptance (stored in localStorage before Keycloak
   * login) to a server-side record. The dedicated GET /user/{id} side-effect that used to
   * persist this is no longer hit, since users are now loaded via the cached/websocket data
   * manager, so persist it explicitly here once the user is authenticated.
   */
  private persistCopilotDisclaimerAcceptance(): void {
    const pathname = window.location.pathname;
    const isDisclaimerPage = DefaultConfig.keycloak.disclaimerPagePath === pathname || pathname.startsWith(DefaultConfig.keycloak.disclaimerPagePath);
    if (isDisclaimerPage || localStorage.getItem('aiDisclaimerApproved') !== 'true') {
      return;
    }

    void this.userService.confirmCopilotDisclaimer()
      .catch(err => this.log.error('Failed to persist AI prototype disclaimer acceptance', err));
  }
}

// Add this for flatpickr localization
if (typeof window !== 'undefined') {
  flatpickr.l10ns.default.firstDayOfWeek = 1; // Monday
}
